import type { AudioEngine } from '../audio/engine';
import { updateStatus } from '../ui/status';
import { generateSampleId } from '../utils/id-generator';

export interface LoadedSample {
  id: string;
  name: string;
  file: File;
}

/**
 * Add a new sample to the loaded samples list
 * NOTE: This is a simplified version. Full implementation needed with all state management.
 */
export async function addSample(
  file: File,
  loadedSamples: LoadedSample[],
  audioEngine: AudioEngine,
  renderSamplesList: () => void,
  updateDropZoneState: () => void
): Promise<{ loadedSamples: LoadedSample[]; activeSampleId: string; currentFile: File | null }> {
  const id = generateSampleId();
  const sample: LoadedSample = { id, name: file.name, file };
  const newLoadedSamples = [...loadedSamples, sample];

  // If this is the first sample, load it
  if (newLoadedSamples.length === 1) {
    try {
      await audioEngine.loadSample(file);
      updateStatus(`Loaded: ${file.name}`, false);
    } catch (error) {
      updateStatus('Error loading sample', false);
      throw error;
    }
  }

  renderSamplesList();
  updateDropZoneState();

  return {
    loadedSamples: newLoadedSamples,
    activeSampleId: newLoadedSamples.length === 1 ? id : loadedSamples.length > 0 ? loadedSamples[0].id : id,
    currentFile: newLoadedSamples.length === 1 ? file : null
  };
}

/**
 * Remove a sample from the list
 */
export function removeSample(
  id: string,
  loadedSamples: LoadedSample[],
  activeSampleId: string | null,
  renderSamplesList: () => void,
  updateDropZoneState: () => void
): { loadedSamples: LoadedSample[]; activeSampleId: string | null } {
  const newLoadedSamples = loadedSamples.filter(s => s.id !== id);
  let newActiveSampleId = activeSampleId;

  if (id === activeSampleId && newLoadedSamples.length > 0) {
    newActiveSampleId = newLoadedSamples[0].id;
  } else if (newLoadedSamples.length === 0) {
    newActiveSampleId = null;
  }

  renderSamplesList();
  updateDropZoneState();

  return { loadedSamples: newLoadedSamples, activeSampleId: newActiveSampleId };
}

/**
 * Render the samples list UI
 */
export function renderSamplesList(
  loadedSamples: LoadedSample[],
  activeSampleId: string | null,
  onRemove: (id: string) => void,
  onSwitch: (id: string) => void
): void {
  const listEl = document.getElementById('samples-list');
  if (!listEl) return;

  if (loadedSamples.length === 0) {
    listEl.innerHTML = '<div class="empty-samples">No samples loaded</div>';
    return;
  }

  listEl.innerHTML = loadedSamples.map(sample => `
    <div class="sample-item ${sample.id === activeSampleId ? 'active' : ''}" data-sample-id="${sample.id}">
      <span class="sample-name">${sample.name}</span>
      <button class="sample-remove" data-remove-id="${sample.id}" title="Remove">×</button>
    </div>
  `).join('');

  listEl.querySelectorAll('[data-remove-id]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const removeId = (btn as HTMLElement).dataset.removeId;
      if (removeId) onRemove(removeId);
    });
  });

  listEl.querySelectorAll('.sample-item').forEach(item => {
    item.addEventListener('click', () => {
      const sampleId = (item as HTMLElement).dataset.sampleId;
      if (sampleId && sampleId !== activeSampleId) {
        onSwitch(sampleId);
      }
    });
  });
}

/**
 * Update drop zone state - shows/hides the upload message overlay
 */
export function updateDropZoneState(hasLoadedSamples: boolean): void {
  const dropZone = document.getElementById('drop-zone');
  
  if (dropZone) {
    // Toggle no-sample class to show/hide the drop overlay
    dropZone.classList.toggle('no-sample', !hasLoadedSamples);
  }
}
